import { CONSTANT } from '@const';
import {
	coordinateDecimals,
	DeviceReadingUnitMeasureOption,
	DocumentsForClientOption,
	InspectionOutcomeOption,
	makeOther,
	modelOther,
	ModuleAForm01Values,
	ModuleAForm02Values,
	ModuleAForm03Values,
	ModuleAForm05Values,
	ModuleAForm06Values,
	ModuleAForm07Values,
	ModuleAFormValues,
	PhotoSubject,
	TamperSealFormValues,
} from '@moduleAForm';
import {
	CertificateItem,
	ChangedDeviceDetails,
	DeviceDetails,
	DeviceMakeModelItem,
	InspectionOutcome,
	MCCSummaryItem,
	Photo,
	ReasonForInspection,
	UpdateAction,
	UpdateCertificatePayload,
} from '@types';
import {
	convertDateToDateValue,
	convertDatetoStrYYYYMMDD,
	convertDateValueToDate,
	FileUploadItem,
	isValidUUID,
	NestedArrayForm,
} from '@utils';
import updateCertificatePayloadEmpty from '@utils/sampleData/forms/updateCertificatePayloadEmpty';
import { isEmpty } from 'lodash';

import { CertificateCreationMapper } from './CertificateCreationMapper';
import { Mapper } from './Mapper';
import { PhotoMapper } from './PhotoMapper';
import { ReadingMapper } from './ReadingMapper';
import { SealMapper } from './SealMapper';
import { SensorMapper } from './SensorMapper';

export class CertificateMapper extends Mapper<
	ModuleAFormValues,
	CertificateItem,
	UpdateCertificatePayload
> {
	private deviceMakesModelsData: DeviceMakeModelItem[];
	private certificateCreationMapper = new CertificateCreationMapper({} as MCCSummaryItem);
	private photoMapper = new PhotoMapper();
	private readingMapper = new ReadingMapper();
	private sealMapper = new SealMapper();
	private sensorMapper: SensorMapper;

	// TODO: (uplift) Remove flag when old form is removed
	private isFormV9: boolean; // Temp flag to run conditionally run code depending on current form

	constructor(
		deviceMakesModelsData: DeviceMakeModelItem[],
		sensorMakesModelsData: DeviceMakeModelItem[],
		isFormV9 = false,
	) {
		super();
		this.isFormV9 = isFormV9;
		this.deviceMakesModelsData = deviceMakesModelsData;
		this.sensorMapper = new SensorMapper(sensorMakesModelsData);
	}

	mapToForm(data: CertificateItem): ModuleAFormValues {
		const isNewMeterCertificate = data.reasonForInspection !== ReasonForInspection.REVALIDATION;
		return {
			form01: this.mapInForm01(data),
			form02: isNewMeterCertificate
				? this.mapInForm02(data)
				: ({} as unknown as ModuleAForm02Values),
			form03: !isNewMeterCertificate
				? this.mapInForm03(data)
				: ({} as unknown as ModuleAForm03Values),
			form04: this.mapInForm04(data),
			form05: {} as unknown as ModuleAForm05Values, // custom restore in moduleAForm
			form06: this.mapInForm06(data),
			form07: this.mapInForm07(data),
		};
	}

	mapFormToPayload(
		moduleAFormValues: Partial<ModuleAFormValues>,
	): Partial<UpdateCertificatePayload> {
		const { form02, form03, form04, form05, form06, form07 } = moduleAFormValues; // exclude form01

		const mappedForm02 = form02 ? this.mapOutForm02(form02) : {};
		const mappedForm03 = form03 ? this.mapOutForm03(form03) : {};
		const mappedForm04 = form04 ? this.mapOutForm04(form04) : {};
		const mappedForm05 = form05 ? this.mapOutForm05(form05) : {};
		const mappedForm06 = form06 ? this.mapOutForm06(form06) : {};
		const mappedForm07 = form07 ? this.mapOutForm07(form07) : {};

		return {
			action: UpdateAction.UPDATE,
			...mappedForm02,
			...mappedForm03,
			...mappedForm04,
			...mappedForm05,
			...mappedForm06,
			...mappedForm07,
		};
	}

	mapModelToPayload(data: CertificateItem): UpdateCertificatePayload {
		return {
			...updateCertificatePayloadEmpty,
			action: UpdateAction.NONE,
			submit: undefined,
			newDeviceDetails: data.newDeviceDetails,
			changedExistingDeviceDetails: data.changedExistingDeviceDetails,
			compliantMD: data.compliantMD,
			nonComplianceStatement: data.nonComplianceStatement,
			modificationsRequired: data.modificationsRequired,
			inspectedDate: data.inspectedDate,
			inspectionOutcome: data.inspectionOutcome,
			configurationSettingsShared: data.configurationSettingsShared,
			openChannelCalibrationReportShared: data.openChannelCalibrationReportShared,
			mdComponents: this.sensorMapper.mapModelToPayloadList(data.mdComponents ?? []),
			seals: this.sealMapper.mapModelToPayloadList(data.seals ?? []),
			photos: this.photoMapper.mapModelToPayloadList(data.photos ?? []),
			reading: this.readingMapper.mapModelToPayload(data.existingDeviceReading ?? {}),
		};
	}

	/*
	 * Mappings to inside
	 */

	private mapInForm01(data: CertificateItem): ModuleAForm01Values {
		return this.certificateCreationMapper.mapToForm(data);
	}

	private mapInForm02(data: CertificateItem): ModuleAForm02Values {
		let makeModelData = this.extractMakeModelFromDevice(data.newDeviceDetails);
		makeModelData = makeModelData ?? this.extractMakeModelFromDevice(data.newDeviceDetails); // prettier-ignore
		const mappedReading = this.readingMapper.mapToForm(data.existingDeviceReading ?? {});

		return {
			field2: makeModelData?.deviceType ?? '',
			field3: makeModelData?.make ?? '',
			field4: makeModelData?.model ?? '',
			field5: data.newDeviceDetails?.serialNumber ?? '',
			field7: data.newDeviceDetails?.size,
			field10: mappedReading.readingValue,
			field11: mappedReading.readingUnit,
			field12: this.formatCoordinate(data.newDeviceDetails?.latitude),
			field13: this.formatCoordinate(data.newDeviceDetails?.longitude),
			field14: data.newDeviceDetails?.gpsDatum ?? '',
			field15: this.sensorMapper.mapToFormList(data.mdComponents ?? [], 'new'),
		};
	}

	private mapInForm03(data: CertificateItem): ModuleAForm03Values {
		const { deviceType, make, model, otherMake, otherModel } = this.extractDeviceTypeAndMakeModel(data); // prettier-ignore
		const mappedReading = this.readingMapper.mapToForm(data.existingDeviceReading ?? {});

		return {
			field1: data.compliantMD as unknown as boolean,
			field2: deviceType ?? '',
			field3: make ?? '',
			field4: otherMake ?? '',
			field5: model ?? '',
			field6: otherModel ?? '',
			field7: data.changedExistingDeviceDetails?.serialNumber ?? data.existingDeviceDetails?.serialNumber ?? '', // prettier-ignore
			field9: data.changedExistingDeviceDetails?.size ?? data.existingDeviceDetails?.size, // prettier-ignore
			field12: mappedReading.readingValue,
			field13: mappedReading.readingUnit,
			field14: this.formatCoordinate(data.changedExistingDeviceDetails?.latitude ?? data.existingDeviceDetails?.latitude), // prettier-ignore
			field15: this.formatCoordinate(data.changedExistingDeviceDetails?.longitude ?? data.existingDeviceDetails?.longitude), // prettier-ignore
			field16: data.changedExistingDeviceDetails?.gpsDatum ?? data.existingDeviceDetails?.gpsDatum ?? '', // prettier-ignore
			field17: this.sensorMapper.mapToFormList(data.mdComponents ?? [], 'changeExisting'),
			field18: this.sensorMapper.mapToFormList(data.mdComponents ?? [], 'new'),
		};
	}

	public mapInExistingDeviceDetails(data: CertificateItem): ModuleAForm03Values {
		const makeModelData = this.extractMakeModelFromDevice(data.existingDeviceDetails);

		return {
			field1: undefined as unknown as boolean,
			field2: makeModelData?.deviceType ?? '',
			field3: makeModelData?.make ?? '',
			field4: undefined as unknown as string,
			field5: makeModelData?.model ?? '',
			field6: undefined as unknown as string,
			field7: data.existingDeviceDetails?.serialNumber ?? '', // prettier-ignore
			field9: data.existingDeviceDetails?.size, // prettier-ignore
			field12: undefined as unknown as string,
			field13: data.existingDeviceReading?.unitOfMeasure as unknown as DeviceReadingUnitMeasureOption, // prettier-ignore
			field14: this.formatCoordinate(data.existingDeviceDetails?.latitude),
			field15: this.formatCoordinate(data.existingDeviceDetails?.longitude),
			field16: data.existingDeviceDetails?.gpsDatum ?? '', // prettier-ignore
			field17: this.sensorMapper.mapToFormList(data.mdComponents ?? [], 'changeExisting'),
			field18: [],
		};
	}

	public extractMakeModelFromDevice(deviceDetails?: DeviceDetails) {
		const makeModelId = deviceDetails?.makeAndModelType;
		return this.deviceMakesModelsData.find(item => item.id === makeModelId);
	}

	private extractDeviceTypeAndMakeModel(data: CertificateItem): {
		deviceType: string | undefined;
		make: string | undefined;
		model: string | undefined;
		otherMake: string | undefined;
		otherModel: string | undefined;
	} {
		let deviceType = undefined;
		let make = undefined;
		let model = undefined;
		let otherMake = undefined;
		let otherModel = undefined;

		const hasChangedDeviceDetails =  !isEmpty(data.changedExistingDeviceDetails?.changeMake) && !isEmpty(data.changedExistingDeviceDetails?.changeModel) && !isEmpty(data.changedExistingDeviceDetails?.changeDeviceType); // prettier-ignore
		const hasChangedMakeAndModelType = isValidUUID(
			data.changedExistingDeviceDetails?.makeAndModelType,
		);
		const hasExistingMakeAndModelType = isValidUUID(data.existingDeviceDetails?.makeAndModelType);
		const isAccurate = Boolean(data.compliantMD);

		if (hasChangedDeviceDetails && !isAccurate) {
			deviceType = data.changedExistingDeviceDetails?.changeDeviceType;
			make = data.changedExistingDeviceDetails?.changeMake ? makeOther : undefined;
			otherMake = data.changedExistingDeviceDetails?.changeMake;
			model = data.changedExistingDeviceDetails?.changeModel ? modelOther : undefined;
			otherModel = data.changedExistingDeviceDetails?.changeModel;
		} else if (hasChangedMakeAndModelType && !isAccurate) {
			const changedExistingMakeModelData = this.extractMakeModelFromDevice(
				data.changedExistingDeviceDetails,
			);
			deviceType = changedExistingMakeModelData?.deviceType;
			make = changedExistingMakeModelData?.make;
			model = changedExistingMakeModelData?.model;
		} else if (hasExistingMakeAndModelType) {
			const existingMakeModelData = this.extractMakeModelFromDevice(data.existingDeviceDetails);
			deviceType = existingMakeModelData?.deviceType;
			make = existingMakeModelData?.make;
			model = existingMakeModelData?.model;
		}

		return { deviceType, make, model, otherMake, otherModel };
	}

	private mapInForm04(data: CertificateItem): NestedArrayForm<TamperSealFormValues> {
		return {
			list: this.sealMapper.mapToFormList(data.seals ?? []),
		};
	}

	public mapInForm05(data: CertificateItem): ModuleAForm05Values {
		const photos = data.photos || [];
		const groupPhotosBySubject = (photos: Photo[]): Record<number, FileUploadItem[]> => {
			return photos.reduce(
				(acc, photo) => {
					if (photo.subject) {
						if (!acc[photo.subject]) {
							acc[photo.subject] = [];
						}
						acc[photo.subject].push(this.photoMapper.mapToForm(photo));
					}
					return acc;
				},
				{} as Record<number, FileUploadItem[]>,
			);
		};

		const groupedPhotos = groupPhotosBySubject(photos);

		return {
			field1: groupedPhotos[PhotoSubject.SerialNumber] || [],
			field2: groupedPhotos[PhotoSubject.DialsOrDisplayIncludingReading] || [],
			field3: groupedPhotos[PhotoSubject.UpstreamExtent] || [],
			field4: groupedPhotos[PhotoSubject.DownstreamExtent] || [],
			field5: groupedPhotos[PhotoSubject.OverallSite] || [],
			field6: groupedPhotos[PhotoSubject.DisposedMeterFinalReading] || [],
			field7: groupedPhotos[PhotoSubject.DisposedMeterSerialNumber] || [],
		};
	}

	private mapInForm06(data: CertificateItem): ModuleAForm06Values {
		return {
			field1: this.parseInspectedDate(data.inspectedDate),
			field2: data.inspectionOutcome as unknown as InspectionOutcomeOption,
			field3: this.mapInForm06Field3(data),
			field4: data.nonComplianceStatement,
			field5: data.modificationsRequired,
		};
	}

	// eslint-disable-next-line @typescript-eslint/no-unused-vars
	private mapInForm07(data: CertificateItem): ModuleAForm07Values {
		const isSubmitted = data.status === CertificateItem.status.SUBMITTED;
		return {
			field1: isSubmitted ? true : (undefined as unknown as boolean), // force undefined
		};
	}

	private mapInForm06Field3(data: CertificateItem): DocumentsForClientOption[] {
		const result = [];
		if (data.diagnosticsRecordShared) {
			result.push(DocumentsForClientOption.Option1);
		}
		if (data.configurationSettingsShared) {
			result.push(DocumentsForClientOption.Option2);
		}
		if (data.openChannelCalibrationReportShared) {
			result.push(DocumentsForClientOption.Option3);
		}
		return result;
	}

	/*
	 * Mappings to outside
	 */

	private mapOutForm02(formValues: ModuleAForm02Values): Partial<UpdateCertificatePayload> {
		const {
			field2,
			field3,
			field4,
			field5,
			field7,
			field10,
			field11,
			field12,
			field13,
			field14,
			field15,
		} = formValues; // deconstruction lets see missed, unmapped fields

		const identifiedMakeModel = this.findDeviceMakeModel(field2, field3, field4);

		return {
			reading: this.readingMapper.mapFormToPayload({
				readingValue: field10,
				readingUnit: field11,
			}),
			newDeviceDetails: {
				makeAndModelType: identifiedMakeModel?.id ?? CONSTANT.INVALID_UUID,
				serialNumber: field5,
				size: field7,
				latitude: field12?.length ? Number(field12) : undefined,
				longitude: field13?.length ? Number(field13) : undefined,
				gpsDatum: field14,
			},
			mdComponents: this.sensorMapper.mapFormToPayloadList(field15 ?? [], 'new'),
		};
	}

	private mapOutForm03(formValues: ModuleAForm03Values): Partial<UpdateCertificatePayload> {
		const {
			field1,
			field2,
			field3,
			field4,
			field5,
			field6,
			field7,
			field9,
			field12,
			field13,
			field14,
			field15,
			field16,
			field17,
			field18,
		} = formValues; // deconstruction lets see missed, unmapped fields

		const isAccurate = Boolean(field1); // when accucary visiliby rules were not met, this accucary field is hidden but implicitily needs to be false
		let changedExistingDeviceDetails: ChangedDeviceDetails = {};

		if (!isAccurate) {
			const identifiedMakeModel =
				field3 !== makeOther && field5 !== modelOther
					? this.findDeviceMakeModel(field2, field3, field5)
					: undefined;

			changedExistingDeviceDetails = {
				makeAndModelType:
					field3 === makeOther || field5 === modelOther
						? CONSTANT.INVALID_UUID
						: identifiedMakeModel?.id,
				serialNumber: field7,
				size: field9,
				latitude: field14?.length ? Number(field14) : undefined,
				longitude: field15?.length ? Number(field15) : undefined,
				gpsDatum: field16,
				changeMake: field3 === makeOther ? field4 : field5 === modelOther ? field3 : undefined,
				changeModel: field5 === modelOther ? field6 : undefined,
				changeDeviceType:
					field3 === makeOther || field5 === modelOther
						? this.mapStringToEnum(field2, DeviceMakeModelItem.deviceType)
						: undefined,
			};
		}

		return {
			compliantMD: isAccurate,
			reading: this.readingMapper.mapFormToPayload({
				readingValue: field12,
				readingUnit: field13,
			}),
			changedExistingDeviceDetails: changedExistingDeviceDetails,
			mdComponents: [
				...this.sensorMapper.mapFormToPayloadList(field17, 'changeExisting'),
				...this.sensorMapper.mapFormToPayloadList(field18, 'new'),
			],
		};
	}

	private mapOutForm04(
		formValues: NestedArrayForm<TamperSealFormValues>,
	): Partial<UpdateCertificatePayload> {
		return {
			action: UpdateAction.NONE,
			seals: this.sealMapper.mapFormToPayloadList(formValues.list ?? []),
		};
	}

	private mapOutForm05(formValues: ModuleAForm05Values): Partial<UpdateCertificatePayload> {
		const { field1, field2, field3, field4, field5, field6, field7 } = formValues;

		const addPhotoSubjectAndNumber = (filesFormValues: FileUploadItem[], subject: PhotoSubject) => {
			return filesFormValues.map((formValue, index) =>
				this.photoMapper.mapFormToPayload({
					formValue,
					subject,
					number: index + 1,
				}),
			);
		};

		const photos = [
			...addPhotoSubjectAndNumber(field1, PhotoSubject.SerialNumber), // prettier-ignore
			...addPhotoSubjectAndNumber(field2, PhotoSubject.DialsOrDisplayIncludingReading), // prettier-ignore
			...addPhotoSubjectAndNumber(field3, PhotoSubject.UpstreamExtent), // prettier-ignore
			...addPhotoSubjectAndNumber(field4, PhotoSubject.DownstreamExtent), // prettier-ignore
			...addPhotoSubjectAndNumber(field5, PhotoSubject.OverallSite), // prettier-ignore
			...addPhotoSubjectAndNumber(field6 ?? [], PhotoSubject.DisposedMeterFinalReading), // prettier-ignore
			...addPhotoSubjectAndNumber(field7 ?? [], PhotoSubject.DisposedMeterSerialNumber), // prettier-ignore
		];

		return {
			action: UpdateAction.NONE,
			photos,
		};
	}

	private mapOutForm06(formValues: ModuleAForm06Values): Partial<UpdateCertificatePayload> {
		return {
			inspectedDate: convertDatetoStrYYYYMMDD(convertDateValueToDate(formValues.field1)),
			inspectionOutcome: this.mapStringToEnum(formValues.field2 as unknown as string, InspectionOutcome), // prettier-ignore
			...this.mapOutForm06Field3(formValues.field3),
			nonComplianceStatement: formValues.field4,
			modificationsRequired: formValues.field5,
		};
	}

	private mapOutForm07(formValues: ModuleAForm07Values): Partial<UpdateCertificatePayload> {
		return {
			action: formValues.field1 ? UpdateAction.UPDATE : UpdateAction.NONE,
			submit: formValues.field1,
		};
	}

	private mapOutForm06Field3(
		field3?: DocumentsForClientOption[],
	): Partial<UpdateCertificatePayload> {
		return !field3
			? {}
			: {
					diagnosticsRecordShared: field3.includes(DocumentsForClientOption.Option1),
					configurationSettingsShared: field3.includes(DocumentsForClientOption.Option2),
					openChannelCalibrationReportShared: field3.includes(DocumentsForClientOption.Option3),
				};
	}

	private findDeviceMakeModel(deviceType: string, make: string, model: string) {
		return this.deviceMakesModelsData.find(itemData => {
			const isMatchByDeviceType = itemData.deviceType === deviceType;
			const isMatchByMake = itemData.make === make;
			const isMatchByModel = itemData.model === model;
			return isMatchByDeviceType && isMatchByMake && isMatchByModel;
		});
	}

	private formatCoordinate(value: number | undefined): string {
		return this.formatNumber(value, coordinateDecimals, true);
	}

	private parseInspectedDate(value: string | undefined) {
		if (!value) {
			return undefined as unknown as Date;
		}
		return this.isFormV9 ? convertDateToDateValue(new Date(value)) : new Date(value);
	}
}
